import { useState } from "react";
import axios from "axios";
import { Mail, Send, AlertTriangle, CheckCircle2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NOT_FOUND = "Email not found or incorrect — please visit the OPS desk to update your details.";

export function SelfServiceEmailLink({ festivalId, token, verifyKey, isLight }) {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null); // { email, valid }
  const [wrong, setWrong] = useState(false);
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [err, setErr] = useState("");
  const qs = verifyKey ? `?vk=${encodeURIComponent(verifyKey)}` : "";
  const url = `${BACKEND}/api/festival/${festivalId}/self-service/${token}/email-link${qs}`;

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true); setWrong(false); setState("idle"); setErr("");
    try { setInfo((await axios.get(url)).data); } catch { setInfo({ email: null, valid: false }); }
  };
  const send = async () => {
    setState("sending"); setErr("");
    try { await axios.post(url); setState("sent"); }
    catch (e) { setErr(e?.response?.data?.detail || "Couldn't send right now — please try again."); setState("error"); }
  };

  const showNotFound = info && (!info.valid || wrong);
  const box = isLight ? "border-[#D4D4D4] bg-white" : "border-[#262626] bg-[#141414]";
  const muted = isLight ? "text-[#525252]" : "text-[#A3A3A3]";

  return (
    <div className="mt-8" data-testid="ss-email-link-section">
      <button type="button" onClick={toggle} data-testid="ss-email-link"
        className={`w-full py-3 border text-xs uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors ${open ? "border-[#CCFF00] text-[#CCFF00]" : `${isLight ? "border-[#D4D4D4]" : "border-[#262626]"} ${muted} hover:border-[#CCFF00] hover:text-[#CCFF00]`}`}>
        <Mail className="w-4 h-4" /> Send link to my email
      </button>
      {open && (
        <div className={`border border-t-0 p-3 space-y-2 ${box}`} data-testid="ss-email-link-panel">
          {!info ? (
            <div className={`text-xs ${muted}`}>Loading…</div>
          ) : showNotFound ? (
            <div className="flex items-start gap-2 text-xs text-[#FFB020]" data-testid="ss-email-not-found">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {NOT_FOUND}
            </div>
          ) : state === "sent" ? (
            <div className="flex items-start gap-2 text-xs text-[#CCFF00]" data-testid="ss-email-sent">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> Link sent to {info.email} — check your inbox.
            </div>
          ) : (
            <>
              <label className={`block text-[10px] uppercase tracking-widest ${muted}`}>Your email</label>
              <input type="email" value={info.email || ""} readOnly aria-readonly="true" data-testid="ss-email-input"
                className={`w-full px-3 py-2 text-sm border cursor-not-allowed ${isLight ? "bg-[#F5F5F5] border-[#D4D4D4] text-[#0A0A0A]" : "bg-[#0A0A0A] border-[#262626] text-white"}`} />
              {err && <div className="text-[11px] text-[#FF4500]" data-testid="ss-email-error">{err}</div>}
              <button type="button" onClick={send} disabled={state === "sending"} data-testid="ss-email-send"
                className="w-full py-2.5 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                <Send className="w-3.5 h-3.5" /> {state === "sending" ? "Sending…" : "Send"}
              </button>
              <button type="button" onClick={() => setWrong(true)} data-testid="ss-email-not-mine"
                className={`text-[11px] underline ${muted} hover:text-[#CCFF00]`}>
                This isn't my email
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
